import globalMessage from "@/utils/globalMessage";
import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Tabs, Form, Input, Button, Spin, Modal, Row, Col } from "antd";
import { api } from "@redux/api";
import RecordAuditTimeline from "@components/Table/RecordAuditLog";
import { useSelector } from "react-redux";
import moment from "moment";

import { useKeepAliveContext } from "@/hooks/use-keep-alive-context";
import { useAuth, AUTH_LEVEL } from "@/hooks/use-auth";
import "./index.css";

const { TabPane } = Tabs;

const EquipmentCategoryDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const { closeTab, refreshTab } = useKeepAliveContext();
  const canWrite = useAuth('equipmentCategory', AUTH_LEVEL.WRITE);
  const canFullAccess = useAuth('equipmentCategory', AUTH_LEVEL.FULL_ACCESS);

  const [loading, setLoading] = useState(true);
  const [readOnly, setReadOnly] = useState(true);
  const [detailData, setDetailData] = useState(null);
  const [activeTabKey, setActiveTabKey] = useState("1");

  const userName = useSelector(state =>
    state?.Auth?.user ||
    state?.Auth?.first_name + " " + state?.Auth?.last_name
  );

  useEffect(() => {
    if (id) {
      fetchDetail();
    }
  }, [id]);

  const fetchDetail = async () => {
    try {
      setLoading(true);
      const response = await api.filterEquipmentCategory(`id=${id}`);
      if (response.status === 200 && response.data.results.length > 0) {
        const data = response.data.results[0];
        setDetailData(data);
        form.setFieldsValue({
          ...data,
          created_date: data.created_date ? moment(data.created_date).format("YYYY-MM-DD HH:mm:ss") : "",
          modified_date: data.modified_date ? moment(data.modified_date).format("YYYY-MM-DD HH:mm:ss") : "",
        });
      } else {
        message.error("Equipment category not found");
      }
    } catch (error) {
      console.error("Error fetching equipment category:", error);
      message.error("Failed to load equipment category details");
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = () => setReadOnly(false);

  const handleCancel = () => {
    setReadOnly(true);
    if (detailData) {
      form.setFieldsValue({
        ...detailData,
        created_date: detailData.created_date ? moment(detailData.created_date).format("YYYY-MM-DD HH:mm:ss") : "",
        modified_date: detailData.modified_date ? moment(detailData.modified_date).format("YYYY-MM-DD HH:mm:ss") : "",
      });
    }
  };

  const handleSubmit = async (values) => {
    try {
      setLoading(true);
      const submitValues = { ...values };
      submitValues.modifiedby_name = userName;
      const response = await api.editEquipmentCategory(id, submitValues);
      if (response.status === 200) {
        globalMessage.success({ message: "Success", description: "Equipment category updated successfully" });
        setReadOnly(true);
        fetchDetail();
        refreshTab("/admin/equipment-category");
      } else {
        globalMessage.error({ message: "Error", description: "Failed to update equipment category" });
      }
    } catch (error) {
      console.error("Error updating equipment category:", error);
      globalMessage.error({ message: "Error", description: "Failed to update equipment category" });
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = () => {
    Modal.confirm({
      title: "Are you sure you want to delete this equipment category?",
      content: "This action cannot be undone.",
      okText: "Yes",
      okType: "danger",
      cancelText: "No",
      onOk: async () => {
        try {
          setLoading(true);
          const response = await api.deleteEquipmentCategory(id);
          if (response.status === 204 || response.status === 200) {
            globalMessage.success({ message: "Success", description: "Equipment category deleted successfully" });
            refreshTab("/admin/equipment-category");
            closeTab();
            navigate("/admin/equipment-category");
          } else {
            globalMessage.error({ message: "Error", description: "Failed to delete equipment category" });
          }
        } catch (error) {
          console.error("Error deleting equipment category:", error);
          globalMessage.error({ message: "Error", description: "Failed to delete equipment category" });
        } finally {
          setLoading(false);
        }
      },
    });
  };

  const renderOperationButton = () => {
    if (readOnly) {
      return (
        <div style={{ display: 'flex', gap: '8px' }}>
          {canWrite && <Button type="primary" onClick={handleEdit}>
            <i className="fa-solid fa-pen-to-square pr-2"></i>Edit
          </Button>}
          {canFullAccess && <Button danger onClick={handleDelete}>
            <i className="fa-solid fa-trash pr-2"></i>Delete
          </Button>}
        </div>
      );
    } else {
      return (
        <div style={{ display: 'flex', gap: '8px' }}>
          <Button type="primary" onClick={() => form.submit()}>
            <i className="fa-solid fa-floppy-disk pr-2"></i>Save
          </Button>
          <Button onClick={handleCancel}>Cancel</Button>
        </div>
      );
    }
  };

  const renderFormFields = () => (
    <Form
      form={form}
      onFinish={handleSubmit}
      layout="horizontal"
      labelCol={{ span: 8 }}
      wrapperCol={{ span: 16 }}
      className="detailForm"
    >
      <Row gutter={[0, 0]}>
        <Col xs={24} lg={12}>
          <Form.Item label="ID" name="id">
            <Input disabled />
          </Form.Item>
        </Col>
        <Col xs={24} lg={12}>
          <Form.Item label="Name" name="name" rules={[{ required: true, message: "This field is required" }]}>
            <Input disabled={readOnly} allowClear />
          </Form.Item>
        </Col>
        <Col xs={24} lg={12}>
          <Form.Item label="Spec1" name="spec1">
            <Input disabled={readOnly} />
          </Form.Item>
        </Col>
        <Col xs={24} lg={12}>
          <Form.Item label="Spec2" name="spec2">
            <Input disabled={readOnly} />
          </Form.Item>
        </Col>
        <Col xs={24} lg={12}>
          <Form.Item label="Spec3" name="spec3">
            <Input disabled={readOnly} />
          </Form.Item>
        </Col>
        <Col xs={24} lg={12}>
          <Form.Item label="Spec4" name="spec4">
            <Input disabled={readOnly} />
          </Form.Item>
        </Col>
        <Col xs={24} lg={12}>
          <Form.Item label="Spec5" name="spec5">
            <Input disabled={readOnly} />
          </Form.Item>
        </Col>
        <Col xs={24} lg={12}>
          <Form.Item label="Spec6" name="spec6">
            <Input disabled={readOnly} />
          </Form.Item>
        </Col>
        <Col xs={24} lg={12}>
          <Form.Item label="Spec7" name="spec7">
            <Input disabled={readOnly} />
          </Form.Item>
        </Col>
        <Col xs={24} lg={12}>
          <Form.Item label="Spec8" name="spec8">
            <Input disabled={readOnly} />
          </Form.Item>
        </Col>
        <Col xs={24} lg={12}>
          <Form.Item
            label="Life Expectancy (Years)"
            name="life_expectancy"
            rules={[{ pattern: /^[0-9]\d*$/, message: 'Must be a positive integer' }]}
          >
            <Input disabled={readOnly} type="number" min={0} step={1} />
          </Form.Item>
        </Col>
        <Col xs={24} lg={12}>
          <Form.Item label="Comment" name="comment">
            <Input disabled={readOnly} />
          </Form.Item>
        </Col>
        <Col xs={24} lg={24}></Col>
        <Col xs={24} lg={12}>
          <Form.Item label="Created By" name="createdby_name">
            <span>{detailData?.createdby_name}</span>
          </Form.Item>
        </Col>
        <Col xs={24} lg={12}>
          <Form.Item label="Created Date" name="created_date">
            <span>{detailData?.created_date ? moment(detailData.created_date).format("YYYY-MM-DD HH:mm:ss") : ""}</span>
          </Form.Item>
        </Col>
        <Col xs={24} lg={12}>
          <Form.Item label="Modified By" name="lastModified_name">
            <span>{detailData?.lastModified_name}</span>
          </Form.Item>
        </Col>
        <Col xs={24} lg={12}>
          <Form.Item label="Modified Date" name="modified_date">
            <span>{detailData?.modified_date ? moment(detailData.modified_date).format("YYYY-MM-DD HH:mm:ss") : ""}</span>
          </Form.Item>
        </Col>
      </Row>
    </Form>
  );

  if (loading && !detailData) {
    return (
      <div style={{ textAlign: "center", padding: "100px 0" }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div className="equipment-category-detail-container">
      <Spin spinning={loading}>
        <Tabs
          defaultActiveKey="1"
          destroyInactiveTabPane={true}
          activeKey={activeTabKey}
          onChange={setActiveTabKey}
          tabBarExtraContent={activeTabKey === "1" ? renderOperationButton() : null}
        >
          <TabPane
            tab={<span><i className="fa-regular fa-rectangle-list pr-2"></i>Detail</span>}
            key="1"
          >
            <div className="slds-m-around_x-small">
              <div className="px-4">
                <div className="w-full">{renderFormFields()}</div>
              </div>
            </div>
          </TabPane>
          <TabPane
            tab={<span><i className="fa-solid fa-timeline pr-2"></i>Log</span>}
            key="2"
          >
            <div className="px-4">
              <RecordAuditTimeline moduleId={id} module="EquipmentCategory" />
            </div>
          </TabPane>
        </Tabs>
      </Spin>
    </div>
  );
};

export default EquipmentCategoryDetail;
